import { ReactNode } from 'react';
import Link from 'next/link';

import Alert, { AlertButtons } from '@r1-frontend/ui-react/components/Alert';
import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import { SingleArrowDown, SingleArrowUp } from '@r1-frontend/ui-react/components/svg/arrows';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import { ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';

import StubNoData from '~/src/features/lk/components/StubNoData';
import { useElementVisibility } from '~/src/helpers/hooks/useElementVisibility';

const connectText = getPluralize(['подключен', 'подключено', 'подключено']);
const serviceText = getPluralize(['сервис', 'сервиса', 'сервисов']);
const themText = getPluralize(['им', 'ими', 'ими']);

type TServiceCategoryProps = {
    name: string | null,
    description?: string | null,
    category?: string,
    withStub?: boolean,
    withButtonShowAll?: boolean,
    showAll?: boolean,
    onToggleShowAll?: () => void,
    children: ReactNode,
    onShowCategory?: () => void,
    connectedCount?: number,
};

const ServiceCategory = (
    {
        name,
        description,
        category,
        connectedCount,

        withStub,
        withButtonShowAll,
        showAll,
        onToggleShowAll,
        onShowCategory,

        children,
    }: TServiceCategoryProps,
) => {
    const ref = useElementVisibility(() => {
        onShowCategory && onShowCategory();
    });

    return (
        <ListContainer
            data-test={'Showcase__ServiceList'}
            $direction={'column'}
            $size={24}
        >
            <TextWrapper $lastIndent={false}>
                <H3 $inline $withIndent={false}>{name}</H3>
                {description && (
                    <Paragraph4 data-test={'ServiceCategory__description'}>{description}</Paragraph4>
                )}
            </TextWrapper>
            {!!connectedCount && (
                <Alert data-test='ServiceCategory__alert'>
                    <Paragraph4>
                        У вас {connectText(connectedCount)} {connectedCount} {serviceText(connectedCount)}. Управлять {themText(connectedCount)} можно в разделе Мои услуги.
                    </Paragraph4>
                    <AlertButtons>
                        <Link href='/lk/vases' passHref><Button size='small' bType='secondary'>Перейти в Мои услуги</Button></Link>
                    </AlertButtons>
                </Alert>
            )}
            <ListContainer
                ref={ref}
                $direction={'row'}
                $desktop={32}
                $mobile={24}
            >
                {withStub
                    ? (
                        <StubNoData
                            data-test={'ServiceCategory__Stub'}
                            title={'К сожалению, в этой категории нет услуг'}
                            description={'Посмотрите услуги из других категорий'}
                        />
                    )
                    : children
                }
            </ListContainer>
            {withButtonShowAll && (
                <ButtonGroup $justify={'center'}>
                    <Button
                        size={'small'}
                        bType={'secondary'}
                        Icon={showAll ? <SingleArrowUp/> : <SingleArrowDown/>}
                        onClick={onToggleShowAll}
                        data-test={`Showcase__ButtonShowAll__${category}`}
                    >
                        {showAll ? 'Скрыть' : 'Показать все'}
                    </Button>
                </ButtonGroup>
            )}
        </ListContainer>
    );
};

export default ServiceCategory;
